import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { VirtualScroller } from "primereact/virtualscroller";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import {
  loadFlowHistory,
  restoreFlowFromHistory,
} from "../storage/storageProxy";

interface FlowHistoryItem {
  id: string;
  flowId: string;
  flowData: any;
  timestamp: string;
  title?: string | null;
}

interface FlowHistoryDialogProps {
  visible: boolean;
  onHide: () => void;
  flowId: string;
  onRestore: (flowData: any) => void;
}

const FlowHistoryDialog: React.FC<FlowHistoryDialogProps> = ({
  visible,
  onHide,
  flowId,
  onRestore,
}) => {
  const [histories, setHistories] = useState<FlowHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (visible && flowId) {
      loadHistories();
    }
  }, [visible, flowId]);

  const loadHistories = async () => {
    setLoading(true);
    try {
      const historyData = await loadFlowHistory(flowId, false, 200);
      setHistories(historyData);
    } catch (error) {
      console.error("Error loading flow history:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: any) => {
    try {
      let date: Date;

      // Handle Firestore Timestamp objects
      if (timestamp && typeof timestamp === "object" && "toDate" in timestamp) {
        date = timestamp.toDate();
      } else if (typeof timestamp === "string") {
        date = new Date(timestamp);
      } else if (typeof timestamp === "number") {
        date = new Date(timestamp);
      } else {
        return "Unknown Date";
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch {
      return "Unable to parse date";
    }
  };

  const handleRestoreClick = (history: FlowHistoryItem) => {
    confirmDialog({
      message:
        "Are you sure you want to restore the flow from this point in time? This will replace the current flow data.",
      header: "Confirm Restore",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        setRestoring(true);
        try {
          const result = await restoreFlowFromHistory(
            history.flowId,
            history.id,
            false,
          );
          if (result.success && result.flowData) {
            onRestore(result.flowData);
            onHide();
          } else {
            console.error("Failed to restore flow:", result.error);
          }
        } catch (error) {
          console.error("Error restoring flow:", error);
        } finally {
          setRestoring(false);
        }
      },
    });
  };

  const itemTemplate = (history: FlowHistoryItem) => {
    const displayTitle =
      (typeof history.title === "string" && history.title.trim().length > 0
        ? history.title.trim()
        : null) ||
      history.flowData?.name ||
      "Untitled Snapshot";

    return (
      <div className="history-item">
        <div className="history-item-content">
          <div className="history-item-name">{displayTitle}</div>
          <div className="history-item-timestamp">
            {formatTimestamp(history.timestamp)}
          </div>
        </div>
        <Button
          icon="pi pi-replay"
          rounded
          text
          severity="warning"
          onClick={() => handleRestoreClick(history)}
          tooltip="Restore to this version"
          tooltipOptions={{ position: "left" }}
          disabled={restoring}
        />
      </div>
    );
  };

  return (
    <Dialog
      header="Snapshots"
      visible={visible}
      onHide={onHide}
      modal
      style={{ width: "500px" }}
      className="flow-history-dialog"
    >
      {loading ? (
        <div className="history-loading">
          <ProgressSpinner
            style={{ width: "50px", height: "50px" }}
            strokeWidth="4"
            fill="var(--surface-ground)"
            animationDuration=".5s"
          />
          <p>Loading flow history...</p>
        </div>
      ) : histories.length === 0 ? (
        <div className="history-empty">
          <p>No snapshots available for this flow.</p>
          <p className="history-empty-hint">
            Use File → Take Snapshot to save a named snapshot.
          </p>
        </div>
      ) : (
        <>
          <p className="history-description">
            Select a snapshot to restore from. Snapshots are sorted by most
            recent first.
          </p>
          <VirtualScroller
            items={histories}
            itemSize={80}
            itemTemplate={itemTemplate}
            style={{ width: "100%", height: "400px" }}
          />
        </>
      )}

      <div className="p-dialog-footer" style={{ marginTop: "1rem" }}>
        <Button
          label="Close"
          icon="pi pi-times"
          onClick={onHide}
          className="p-button-text"
        />
      </div>

      <ConfirmDialog />
    </Dialog>
  );
};

export default FlowHistoryDialog;
